import { DEFAULT_LIMITS } from "./limits";
import { identifyBlocks, migrateDocument, type Migration } from "./migrations";
import { migrateTokens } from "./tokens";
import type { EditorDocument, SerializedNodeLike } from "./types";
import { validateDocument, type DocumentIssue, type ValidateOptions } from "./validate";

/**
 * The outcome of reading stored content. `unreadable` keeps exactly what was stored so the host can
 * offer it for export; a session in that state never autosaves.
 */
export type OpenResult =
  | {
      status: "ready";
      document: EditorDocument;
      /**
       * The document differs from what was stored: its schema was upgraded, or blocks that had no
       * ID were given one. The session commits such a document once so the change becomes durable.
       */
      migrated: boolean;
    }
  | { status: "unreadable"; raw: string; issue: DocumentIssue };

export interface OpenOptions extends ValidateOptions {
  migrations?: Readonly<Record<number, Migration>>;
}

const encoder = new TextEncoder();

/** Parses, migrates, and validates stored content without ever substituting an empty document. */
export function openDocument(stored: unknown, options: OpenOptions = {}): OpenResult {
  const limits = options.limits ?? DEFAULT_LIMITS;
  let raw: string;
  let parsed: unknown;
  if (typeof stored === "string") {
    raw = stored;
    // Cheap upper bound first: UTF-8 never needs more than three bytes per UTF-16 code unit.
    if (raw.length > limits.maxBytes || encoder.encode(raw).length > limits.maxBytes) {
      return unreadable(raw, {
        code: "limit-exceeded",
        message: `Larger than ${limits.maxBytes} bytes.`,
        limit: "bytes",
      });
    }
    try {
      parsed = JSON.parse(raw);
    } catch (error) {
      return unreadable(raw, {
        code: "invalid-json",
        message: error instanceof Error ? error.message : "Invalid JSON.",
      });
    }
  } else {
    parsed = stored;
    raw = stringifyOriginal(stored);
  }

  const migration = migrateDocument(parsed, options.migrations);
  if (!migration.ok) return unreadable(raw, migration.issue);
  // Content built by hand (a host, an import) may lack block IDs even at the current version.
  const identified = identifyBlocks(migration.document);
  const upgraded = upgradeTokens(identified, options);
  const validation = validateDocument(upgraded, options);
  if (!validation.ok) return unreadable(raw, validation.issue);
  return {
    status: "ready",
    document: validation.document,
    migrated: migration.migrated || upgraded !== migration.document,
  };
}

/** Custom tokens stored at an older version go through their definition's `migrate`. */
function upgradeTokens(
  document: Record<string, unknown>,
  options: OpenOptions,
): Record<string, unknown> {
  const content = document.content as { root?: SerializedNodeLike } | undefined;
  if (options.tokens === undefined || content?.root === undefined) return document;
  if (typeof content.root !== "object" || content.root === null) return document;
  const { root, migrated } = migrateTokens(content.root, options.tokens);
  return migrated === 0 ? document : { ...document, content: { ...content, root } };
}

function unreadable(raw: string, issue: DocumentIssue): OpenResult {
  return { status: "unreadable", raw, issue };
}

function stringifyOriginal(value: unknown): string {
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
}
